export const COLOR_TEMPERATURE_SPECIFICATION_IDS = [41, 49]

export const LIGHTING_COLOR_SPECIFICATION_ID = 1

export const COLOR_TEMPERATURE_SCALE = { min: 2700, max: 7000 }

export const COLOR_TEMPERATURE_REFERENCES = [
  { kelvin: 3200, key: 'halogen' },
  { kelvin: 4300, key: 'xenon' },
  { kelvin: 5500, key: 'daylight' },
]

export const COLOR_TEMPERATURE_TONES = [
  { key: 'yellow', from: 0, sample: 2850, range: '2700 – 3000 K' },
  { key: 'warm', from: 3001, sample: 4000, range: '3000 – 5000 K' },
  { key: 'neutral', from: 5001, sample: 5200, range: '5000 – 5500 K' },
  { key: 'pure', from: 5501, sample: 5800, range: '5500 – 6000 K' },
  { key: 'cool', from: 6001, sample: 6800, range: '6000 – 7000 K' },
]

export const COLOR_TEMPERATURE_PALETTE = [
  [1000, '#FFB000'],
  [2000, '#FFC400'],
  [2700, '#FFD400'],
  [3000, '#FFDE2E'],
  [3500, '#FFE97F'],
  [4000, '#FFF0B0'],
  [4300, '#FFF4C9'],
  [5000, '#FFF9E4'],
  [5500, '#FFFDF4'],
  [6000, '#FFFFFF'],
  [6500, '#F2F7FF'],
  [7000, '#E4EEFF'],
  [8000, '#D6E5FF'],
  [10000, '#C6DBFF'],
  [20000, '#B4CEFF'],
]

const WHITE_HALO = [190, 203, 220]

const clamp = (value, min, max) => Math.min(max, Math.max(min, value))

const hexToRgb = hex =>
  [1, 3, 5].map(index => parseInt(hex.slice(index, index + 2), 16))

const rgba = (color, alpha = 1) => `rgba(${color.join(', ')}, ${alpha})`

export function colorTemperatureTone(kelvin) {
  return COLOR_TEMPERATURE_TONES.filter(tone => kelvin >= tone.from).pop().key
}

const NOMINAL_KELVIN_STEP = 100

const nominalKelvin = kelvin =>
  Math.floor(kelvin / NOMINAL_KELVIN_STEP) * NOMINAL_KELVIN_STEP

export function colorTemperatureToneRanges({ min, max }, values) {
  const tones = COLOR_TEMPERATURE_TONES.map((tone, index) => {
    const next = COLOR_TEMPERATURE_TONES[index + 1]

    return {
      key: tone.key,
      sample: tone.sample,
      from: Math.max(min, tone.from),
      to: next ? Math.min(max, next.from - 1) : max,
      nominal: [
        index ? nominalKelvin(tone.from) : null,
        next ? nominalKelvin(next.from) : null,
      ],
    }
  }).filter(
    tone =>
      tone.from <= tone.to &&
      (!Array.isArray(values) ||
        values.some(value => value >= tone.from && value <= tone.to))
  )

  const coversBounds =
    tones.length === 1 && tones[0].from <= min && tones[0].to >= max

  return coversBounds ? [] : tones
}

export function colorTemperatureToneOf(range, bounds) {
  if (!Array.isArray(range) || !bounds) return null

  const tone = colorTemperatureToneRanges(bounds).find(
    ({ from, to }) => from === range[0] && to === range[1]
  )

  return tone ? tone.key : null
}

export function colorTemperatureToneLabel([from, to], formatNumber) {
  if (from === null) return `< ${formatNumber(to)}\u00a0K`
  if (to === null) return `> ${formatNumber(from)}\u00a0K`

  return `${formatNumber(from)}\u2013${formatNumber(to)}\u00a0K`
}

export function kelvinToRgb(kelvin) {
  const first = COLOR_TEMPERATURE_PALETTE[0][0]
  const last =
    COLOR_TEMPERATURE_PALETTE[COLOR_TEMPERATURE_PALETTE.length - 1][0]
  const value = clamp(Number(kelvin) || first, first, last)
  const index = Math.max(
    1,
    COLOR_TEMPERATURE_PALETTE.findIndex(([stop]) => stop >= value)
  )
  const [fromKelvin, fromHex] = COLOR_TEMPERATURE_PALETTE[index - 1]
  const [toKelvin, toHex] = COLOR_TEMPERATURE_PALETTE[index]
  const ratio = (value - fromKelvin) / (toKelvin - fromKelvin)
  const to = hexToRgb(toHex)

  return hexToRgb(fromHex).map((channel, channelIndex) =>
    Math.round(channel + (to[channelIndex] - channel) * ratio)
  )
}

export function kelvinToCss(kelvin) {
  return `rgb(${kelvinToRgb(kelvin).join(', ')})`
}

export function colorTemperatureHalo(kelvin) {
  const color = kelvinToRgb(kelvin)
  const whiteness = clamp((Math.min(...color) - 200) / 55, 0, 1)

  return color.map((channel, index) =>
    Math.round(channel + (WHITE_HALO[index] - channel) * whiteness)
  )
}

export function colorTemperatureGlowStyle(kelvin, size) {
  const color = kelvinToRgb(kelvin)
  const fill = rgba(color)
  const rim = rgba(color.map(channel => Math.round(channel * 0.9)))
  const halo = rgba(colorTemperatureHalo(kelvin), 0.85)
  const blur = Math.round(size * 0.55)
  const spread = Math.round(size * 0.2)

  return {
    width: `${size}px`,
    height: `${size}px`,
    background: `radial-gradient(circle at 50% 42%, #fff 0%, rgba(255, 255, 255, 0.9) 18%, ${fill} 58%, ${rim} 100%)`,
    boxShadow: `0 0 0 1px rgba(15, 23, 42, 0.14), 0 0 ${blur}px ${spread}px ${halo}`,
  }
}

export function formatColorTemperature(kelvin) {
  return `\u00b1\u00a0${Math.round(kelvin)}\u00a0K`
}

export function colorTemperaturePosition(
  kelvin,
  { min, max } = COLOR_TEMPERATURE_SCALE
) {
  if (max <= min) return 0

  return clamp((kelvin - min) / (max - min), 0, 1) * 100
}

export function colorTemperatureGradient({
  min,
  max,
} = COLOR_TEMPERATURE_SCALE) {
  const kelvins = [
    min,
    ...COLOR_TEMPERATURE_PALETTE.map(([stop]) => stop).filter(
      stop => stop > min && stop < max
    ),
    Math.max(min, max),
  ]
  const stops = kelvins.map((kelvin, index) => {
    const position =
      index === kelvins.length - 1
        ? 100
        : Math.round(colorTemperaturePosition(kelvin, { min, max }) * 100) / 100

    return `${kelvinToCss(kelvin)} ${position}%`
  })

  return `linear-gradient(to right, ${stops.join(', ')})`
}

const SPACES = /[\s\u00a0\u2009\u200b\u202f\u3000]+/g

const KELVIN_MENTIONS =
  /(^|[^0-9.,])(\d{1,3}(?:[ .,]\d{3})+|\d+) ?(kel[vw]in[a-z\u00c0-\u024f]*|[\u03ba\u039a]\u03ad\u03bb\u03b2\u03b9\u03bd[\u0370-\u03ff]*|\u30b1\u30eb\u30d3\u30f3|k)(?![a-z0-9\u00c0-\u024f\u0370-\u03ff])/gi

const TONE_SEPARATORS =
  /\+ ?\/ ?-|[\u00b1\u2248~()[\]{}.,;!?"\u00ab\u00bb\u201c\u201d\u201e\u2026_\-\uff08\uff09\u3001\u3002]/g

const EXTRA_INFORMATION = /[0-9+/:&=\uff1a]|rgb/i

const MAX_TONE_WORDS = 2

export function kelvinMentions(text) {
  const kelvins = []
  const rest = String(text)
    .replace(SPACES, ' ')
    .replace(KELVIN_MENTIONS, (mention, before, number) => {
      kelvins.push(parseInt(number.replace(/[^0-9]/g, ''), 10))
      return `${before} `
    })

  return {
    kelvins: kelvins.filter(
      (kelvin, index) => kelvins.indexOf(kelvin) === index
    ),
    rest: rest.replace(TONE_SEPARATORS, ' ').replace(/ +/g, ' ').trim(),
  }
}

export function restatesColorTemperature(text, kelvin) {
  if (typeof text !== 'string' || !kelvin) return false

  const { kelvins, rest } = kelvinMentions(text)

  return (
    kelvins.length === 1 &&
    kelvins[0] === Math.round(kelvin) &&
    !EXTRA_INFORMATION.test(rest) &&
    (rest === '' || rest.split(' ').length <= MAX_TONE_WORDS)
  )
}

export function duplicatesColorTemperature(value, kelvin) {
  const id = value?.specification?.id

  if (COLOR_TEMPERATURE_SPECIFICATION_IDS.includes(id)) return true

  return (
    id === LIGHTING_COLOR_SPECIFICATION_ID &&
    restatesColorTemperature(value.value, kelvin)
  )
}

export function colorTemperatureWording(values, variant) {
  const kelvin = variant?.color_temperature
  if (!kelvin || !Array.isArray(values)) return null

  const source = values.find(
    value =>
      value?.specification?.id === LIGHTING_COLOR_SPECIFICATION_ID &&
      restatesColorTemperature(value.value, kelvin)
  )
  const words = source ? kelvinMentions(source.value).rest : ''
  if (!words) return null

  const shouting =
    words === words.toUpperCase() && words !== words.toLowerCase()
  const text = shouting ? words.toLowerCase() : words

  return text.charAt(0).toUpperCase() + text.slice(1)
}

export function describeColorTemperature(variant) {
  const value = formatColorTemperature(variant.color_temperature)
  const wording = colorTemperatureWording(
    variant.specification_values_featured,
    variant
  )

  return wording ? `${value} (${wording})` : value
}

export function withoutColorTemperatureSpecifications(values, variant) {
  if (!variant?.color_temperature || !Array.isArray(values)) {
    return values || []
  }

  return values.filter(
    value => !duplicatesColorTemperature(value, variant.color_temperature)
  )
}
